import { useMemo, useState } from 'react';
import { App, Avatar, Button, Card, Select, Space, Tag, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import type { EmailMessageItem, ThreadDetail } from '@crm/shared';
import { RichTextEditor } from './RichTextEditor';
import { useAgents, useReplyThread } from '../features/email/api';
import { errorMessage } from '../api/client';
import { fmtDateTime } from '../utils/format';
import { palette } from '../app/theme';
import { EntityTag } from './EntityTag';

const initials = (s: string) => s.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

const STATUS_COLOR: Record<string, string> = { SENT: 'blue', DELIVERED: 'cyan', OPENED: 'success', CLICKED: 'success', BOUNCED: 'error', COMPLAINED: 'error', FAILED: 'error', QUEUED: 'default', RECEIVED: 'purple' };

export function MessageCard({ m }: { m: EmailMessageItem }) {
  const clean = useMemo(() => (m.html ? DOMPurify.sanitize(m.html, { USE_PROFILES: { html: true } }) : null), [m.html]);
  const when = m.direction === 'OUT' ? m.sentAt ?? m.createdAt : m.receivedAt ?? m.createdAt;
  const sender = m.direction === 'OUT' ? (m.agent?.name ?? m.fromAddress) : m.fromAddress;
  const out = m.direction === 'OUT';
  return (
    <Card
      style={{ marginBottom: 16, background: out ? '#FBFCFF' : palette.surface }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <Avatar size={36} style={{ background: out ? palette.primarySoft : '#F3E8FF', color: out ? palette.primary : '#7C3AED', fontWeight: 600, flex: 'none' }}>{initials(sender)}</Avatar>
          <div style={{ minWidth: 0, lineHeight: 1.35 }}>
            <Typography.Text strong style={{ display: 'block' }} ellipsis>{sender}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }} ellipsis>{`to ${m.toAddresses.join(', ')}`}</Typography.Text>
          </div>
        </div>
      }
      extra={
        <Space size={6}>
          <Tag color={STATUS_COLOR[m.status] ?? 'default'} style={{ marginInlineEnd: 0 }}>{m.status.charAt(0) + m.status.slice(1).toLowerCase()}</Tag>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{fmtDateTime(when)}</Typography.Text>
        </Space>
      }
    >
      {m.error && <Typography.Paragraph type="danger" style={{ fontSize: 12 }}>{m.error}</Typography.Paragraph>}
      {clean ? <div className="email-body" dangerouslySetInnerHTML={{ __html: clean }} /> : <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{m.text}</pre>}
    </Card>
  );
}

export function ThreadView({ thread, showContactLink = true }: { thread: ThreadDetail; showContactLink?: boolean }) {
  const { message } = App.useApp();
  const agents = useAgents();
  const reply = useReplyThread();
  const [html, setHtml] = useState('');
  const active = (agents.data ?? []).filter((a) => a.isActive);
  const [picked, setAgentId] = useState<string | undefined>(undefined);
  // default to the thread's agent, but only while that agent is still active
  const agentId = picked ?? (active.some((a) => a.id === thread.agent?.id) ? thread.agent?.id : undefined);
  const contactName = thread.contact ? [thread.contact.firstName, thread.contact.lastName].filter(Boolean).join(' ') || thread.contact.email : null;

  const send = async () => {
    if (!html.trim()) return;
    try {
      await reply.mutateAsync({ id: thread.id, agentId: agentId ?? undefined, html });
      setHtml('');
      message.success('Reply sent');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.01em' }}>{thread.subject || '(no subject)'}</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {thread.domain.domain}
          {thread.contact && showContactLink && (
            <>
              {' · '}
              <EntityTag kind="contact" id={thread.contact.id}>{contactName}</EntityTag>
            </>
          )}
        </Typography.Text>
      </div>
      {thread.messages.map((m) => <MessageCard key={m.id} m={m} />)}
      {thread.contact ? (
        <Card title={<span style={{ fontSize: 16, fontWeight: 600 }}>Reply</span>}>
          <Space orientation="vertical" style={{ width: '100%' }} size={12}>
            <Select
              style={{ minWidth: 320 }}
              value={agentId}
              onChange={setAgentId}
              placeholder={agents.isLoading ? 'Loading agents…' : active.length ? 'Reply as…' : 'No active agents. Create one under Admin → Agents.'}
              loading={agents.isLoading}
              aria-label="Reply as agent"
              options={active.map((a) => ({ value: a.id, label: `${a.name} <${a.email}>` }))}
            />
            <RichTextEditor value={html} onChange={setHtml} placeholder="Write your reply…" minHeight={140} />
            <div style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<SendOutlined />} onClick={() => void send()} loading={reply.isPending} disabled={!html.trim() || !agentId}>Send reply</Button>
            </div>
          </Space>
        </Card>
      ) : (
        <Typography.Text type="secondary">This thread is not linked to a contact, so it cannot be replied to here.</Typography.Text>
      )}
    </div>
  );
}
